import { useEffect, useMemo, useRef } from "react";
import { UseFormReturn } from "react-hook-form";
import { addDays, addYears, getDate, getMonth } from "date-fns";

import Grid from "@mui/material/Grid";

import {
  DateRange,
  FormCheckbox,
  FormCurrencyField,
  FormCurrentInsurerAutocomplete,
  FormTextField,
} from "@covertree/ui";
import FormPhoneField from "@covertree/ui/forms/FormPhoneField";
import PolicyStatusInfo from "./PolicyStatusInfo";
import { EventEditableFields } from "@redux/event/types";

import { Coverage, CoverageEventType } from "@graphql/api";

interface CoverageDataFormProps {
  editableFields?: EventEditableFields;
  eventType?: CoverageEventType;
  form: UseFormReturn;
  preEventCoverage: Coverage;
}

export function CoverageDataForm({
  editableFields,
  eventType,
  form: {
    control,
    watch,
    trigger,
    formState: { isSubmitting },
    setValue,
  },
  preEventCoverage,
}: CoverageDataFormProps) {
  const isOtherInsuranceCarrier = watch("isOtherInsuranceCarrier");
  const insuranceCarrierName = watch("insuranceCarrierName");
  const otherInsuranceCarrierName = watch("otherInsuranceCarrierName");
  const policyEffectiveDate = watch("policyEffectiveDate");
  const policyEndDate = watch("policyEndDate");

  const otherFieldsDisabled =
    !!editableFields && !editableFields.allOtherFieldsEditable;

  useEffect(() => {
    if (
      eventType === CoverageEventType.Cancellation &&
      policyEndDate >= new Date(`${preEventCoverage.policyEndDate} 00:00:00`)
    ) {
      setValue("policyEndDate", "");
    }

    if (eventType === CoverageEventType.Renewal) {
      const preEventCoverageEndDate = new Date(
        `${preEventCoverage.policyEndDate} 00:00:00`
      );
      const freshPolicyEndDate = new Date(preEventCoverageEndDate.getTime());
      freshPolicyEndDate.setFullYear(preEventCoverageEndDate.getFullYear() + 1);

      setValue("policyEndDate", freshPolicyEndDate);
    }

    if (eventType === CoverageEventType.Reinstatement) {
      // We are looking for the first, because the events are ordered from newest to oldest, and we want the most recent cancellation event
      const cancellationEventIndex = preEventCoverage.events.findIndex(
        ({ type }) => type === CoverageEventType.Cancellation
      );

      if (cancellationEventIndex !== -1) {
        const preCancellationEvent = preEventCoverage.events
          .slice(cancellationEventIndex + 1)
          .find(({ type }) => type !== CoverageEventType.Cancellation);

        if (preCancellationEvent) {
          setValue(
            "policyEndDate",
            new Date(`${preCancellationEvent.eventData.termEndDate} 00:00:00`)
          );
        }
      }
    }
  }, [eventType]);

  useEffect(() => {
    if (!!insuranceCarrierName?.value || !!otherInsuranceCarrierName) {
      trigger(["insuranceCarrierName", "otherInsuranceCarrierName"]);
    }
  }, [
    isOtherInsuranceCarrier,
    insuranceCarrierName,
    otherInsuranceCarrierName,
    trigger,
  ]);

  useEffect(() => {
    if (!!policyEffectiveDate || !!policyEndDate)
      trigger(["policyEffectiveDate", "policyEndDate"]);
  }, [policyEffectiveDate, policyEndDate, trigger]);

  // The end date follows the effective date only when the effective date is changed. An end date the form
  // starts with (pre-filled from a policy document, or entered before going back) is kept.
  const lastEffectiveDate = useRef(policyEffectiveDate);

  useEffect(() => {
    const effectiveDateChanged =
      +(policyEffectiveDate ?? 0) !== +(lastEffectiveDate.current ?? 0);
    lastEffectiveDate.current = policyEffectiveDate;

    if (policyEffectiveDate && !eventType && effectiveDateChanged) {
      const nextYearDate = addYears(policyEffectiveDate, 1);

      // Feb 29 -> March 1 transition
      const isFeb29 =
        getMonth(policyEffectiveDate) === 1 &&
        getDate(policyEffectiveDate) === 29;

      if (isFeb29) {
        setValue("policyEndDate", addDays(nextYearDate, 1));
      } else {
        setValue("policyEndDate", nextYearDate);
      }
    }
  }, [policyEffectiveDate]);

  const minEndDate = useMemo(() => {
    if (eventType === CoverageEventType.Renewal) {
      return new Date(`${preEventCoverage.policyEndDate} 00:00:00`);
    }

    if (eventType === CoverageEventType.Cancellation) {
      return new Date(`${preEventCoverage.policyEffectiveDate} 00:00:00`);
    }

    return undefined;
  }, [eventType, preEventCoverage]);

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <FormCurrentInsurerAutocomplete
          name="insuranceCarrierName"
          control={control}
          disabled={isOtherInsuranceCarrier || otherFieldsDisabled}
        />
      </Grid>
      <Grid item xs={12}>
        <FormCheckbox
          id="isOtherInsuranceCarrier"
          name="isOtherInsuranceCarrier"
          label="Other Insurance Carrier"
          control={control}
          disabled={isSubmitting || otherFieldsDisabled}
        />
      </Grid>
      <Grid item xs={12}>
        <FormTextField
          id="otherInsuranceCarrierName"
          name="otherInsuranceCarrierName"
          label=""
          hiddenLabel
          control={control}
          disabled={
            isSubmitting || !isOtherInsuranceCarrier || otherFieldsDisabled
          }
        />
      </Grid>
      <Grid item xs={12}>
        <FormTextField
          id="policyId"
          name="policyId"
          label="Policy ID"
          control={control}
          disabled={isSubmitting || otherFieldsDisabled}
        />
      </Grid>
      <Grid item xs={12}>
        <FormTextField
          id="description"
          name="description"
          label="Description"
          control={control}
          disabled={
            isSubmitting ||
            (!!editableFields && !editableFields.policyDescriptionEditable)
          }
          optional
        />
      </Grid>
      <Grid item xs={12}>
        <DateRange
          id="policyTerm"
          startDateName="policyEffectiveDate"
          endDateName="policyEndDate"
          label="Policy Term"
          control={control}
          minEndDate={minEndDate}
          maxEndDate={
            eventType === CoverageEventType.Cancellation
              ? new Date(`${preEventCoverage.policyEndDate} 00:00:00`)
              : undefined
          }
          disabledStartRange={
            isSubmitting ||
            (!!editableFields && !editableFields.policyStartDateEditable)
          }
          disabledEndRange={
            isSubmitting ||
            (!!editableFields && !editableFields.policyEndDateEditable)
          }
        />
        <PolicyStatusInfo
          policyEffectiveDate={policyEffectiveDate}
          policyEndDate={policyEndDate}
          tooltip={
            "Policy Status is active when policy was issued. Policy Status is readonly and automatically set based on policy term."
          }
        />
      </Grid>
      <Grid item xs={12}>
        <FormCurrencyField
          id="annualCost"
          name="annualCost"
          label="Annual Cost"
          control={control}
          disabled={isSubmitting || otherFieldsDisabled}
        />
      </Grid>
      <Grid item xs={12}>
        <FormTextField
          id="agencyName"
          name="agencyName"
          label="Agency Name"
          control={control}
          disabled={isSubmitting || otherFieldsDisabled}
          optional
        />
      </Grid>
      <Grid item xs={12}>
        <FormTextField
          id="agencyAddress"
          name="agencyAddress"
          label="Agency Address"
          control={control}
          disabled={isSubmitting || otherFieldsDisabled}
          optional
        />
      </Grid>
      <Grid item xs={12}>
        <FormPhoneField
          id="agencyPhoneNumber"
          name="agencyPhoneNumber"
          label="Agency Phone Number"
          control={control}
          disabled={isSubmitting || otherFieldsDisabled}
          optional
        />
      </Grid>
      <Grid item xs={12}>
        <FormTextField
          id="agencyEmail"
          name="agencyEmail"
          label="Agency Email"
          control={control}
          disabled={isSubmitting || otherFieldsDisabled}
          optional
        />
      </Grid>
      <Grid item xs={12} mt={3} mb={1}>
        <FormCheckbox
          id="isInternalSale"
          name="isInternalSale"
          control={control}
          disabled={isSubmitting || otherFieldsDisabled}
          label="Covertree internal sales agent issued this policy"
        />
      </Grid>
    </Grid>
  );
}
